import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import { lookupAutomationCriterionThunk } from '@spike/automations-action';
import { type CriterionLookup, type CriterionOption } from '@spike/automation-model';
import { Checkbox } from 'components/UI/V3/Forms/Checkbox/Checkbox';
import Spinner from 'components/UI/V3/Spinner';
import styles from './CriterionAccordion.module.scss';

interface Props {
    lookup: CriterionLookup;
    selected: string[];
    onChange: (selected: string[]) => void;
}

/**
 * Search-backed picker for catalogs too large to ship inline (breed is ~380
 * entries per species). `ids` resolves what's already selected, so a saved
 * automation shows labels instead of raw ids; `q` searches as the user types.
 */
const CriterionLookupField: FunctionComponent<Props> = ({ lookup, selected, onChange }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [query, setQuery] = useState('');
    const [results, setResults] = useState<CriterionOption[]>([]);
    const [resolved, setResolved] = useState<CriterionOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);

    const debouncedQuery = useDebounce(query, 350);

    // Held in a ref: useApiClientWrapper() returns a new object every render, so
    // depending on it directly would rebuild `search` each time and refire both
    // effects below in a loop.
    const wrapperRef = useRef(apiClientWrapper);
    wrapperRef.current = apiClientWrapper;

    const search = useCallback(
        (options: { query?: string; ids?: string[] }) =>
            (dispatch as any)(lookupAutomationCriterionThunk(wrapperRef.current, lookup.resource, options)),
        [dispatch, lookup.resource]
    );

    const selectedKey = selected.join(',');
    useEffect(() => {
        if (selected.length === 0) {
            setResolved([]);
            return;
        }

        let cancelled = false;
        search({ ids: selected })
            .then((result: { options: CriterionOption[] }) => {
                if (!cancelled) setResolved(result.options);
            })
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey, search]);

    useEffect(() => {
        if (debouncedQuery.length < lookup.minChars) {
            setResults([]);
            setHasMore(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        search({ query: debouncedQuery })
            .then((result: { options: CriterionOption[]; hasMore: boolean }) => {
                if (cancelled) return;
                setResults(result.options);
                setHasMore(result.hasMore);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [debouncedQuery, lookup.minChars, search]);

    const visible = useMemo(() => {
        const selectedSet = new Set(selected);
        const pinned = resolved.filter(option => selectedSet.has(option.value));
        const rest = results.filter(option => !selectedSet.has(option.value));

        return [...pinned, ...rest];
    }, [resolved, results, selected]);

    const toggle = (value: string) =>
        onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);

    return (
        <div className={styles.lookup}>
            <input
                type="text"
                className={styles.lookupInput}
                placeholder={`Search…`}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
            />

            {loading && <Spinner />}

            {visible.map(option => (
                <Checkbox
                    key={option.value}
                    checked={selected.includes(option.value)}
                    onChange={() => toggle(option.value)}
                    label={option.label}
                />
            ))}

            {!loading && visible.length === 0 && (
                <p className={styles.lookupHint}>
                    {query.length < lookup.minChars
                        ? `Type at least ${lookup.minChars} characters to search.`
                        : 'No matches.'}
                </p>
            )}

            {hasMore && <p className={styles.lookupHint}>Keep typing to narrow these down.</p>}
        </div>
    );
};

export default CriterionLookupField;
